import { AGENT_RUN_PROCESS, CaseState, permissionsForRoles } from '@agentic/contracts';
import {
  Badge,
  Button,
  DeadlineBadge,
  EmptyState,
  FilterBar,
  FilterSelect,
  formatDateTime,
  formatRelative,
  SearchInput,
  Segmented,
  SkeletonRows,
  segmentClass,
  Table,
} from '@agentic/ui-kit';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { z } from 'zod';
import { ow } from '../../../app/outcome-words.ts';
import { usePrincipalNames } from '../../../app/principal-names.tsx';
import { capabilitiesQuery } from '../../../app/queries.ts';
import { QueryError } from '../../../app/route-states.tsx';
import { CARD, PAGE, PageHeader } from '../../../app/shell.tsx';
import { formatShortDate } from '../../../app/short-date.ts';
import { useMe } from '../../../app/use-me.ts';
import {
  caseListPagesQuery,
  openDeadlinesQuery,
  processDefinitionsQuery,
} from '../../../features/cases/api.ts';
import {
  type CaseListSort,
  CaseOutcomeBadge,
  CaseWaitingOnLine,
  sortCaseRows,
} from '../../../features/cases/case-list-row.tsx';
import { CASE_STATE_LABELS } from '../../../features/cases/model.ts';
import { DUE_SOONEST_LABEL, type OutcomeContext } from '../../../features/cases/outcome-model.ts';
import { StartCaseDrawer } from '../../../features/cases/start/start-case-drawer.tsx';
import { WaitingOnYouSection } from '../../../features/cases/waiting-on-you.tsx';
import { type CaseDeadline, caseDeadlines } from '../../../features/cases/wave21-model.ts';

/**
 * `new`: the "New case" drawer is open; a value other than `case` preselects that capability. `view`: "Waiting on
 * you" (`attention=me`), "Mine" (`createdBy=me`) or all cases; `q`: text search; `domain`: domain filter. `sort`:
 * `due` = "Due soonest" (26c, ordered in the browser over the loaded pages: the server has no sort parameter);
 * absent = the server's order, newest first.
 */
const Search = z.object({
  view: z.enum(['waiting', 'mine', 'all']).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  domain: z.string().optional().catch(undefined),
  caseState: CaseState.optional().catch(undefined),
  processType: z.string().optional().catch(undefined),
  sort: z.enum(['due']).optional().catch(undefined),
  new: z.string().optional().catch(undefined),
});

const VIEWS = [
  { id: 'waiting', label: 'Waiting on you' },
  { id: 'mine', label: 'Mine' },
  { id: 'all', label: 'All' },
] as const;

export const Route = createFileRoute('/_app/cases/')({
  validateSearch: Search,
  component: CasesPage,
});

/** Case list (17e, 20a): every case the viewer may view; the server applies entitlements per case. */
function CasesPage() {
  const { caseState: state, processType, new: starting, view = 'all', q, domain, sort } = Route.useSearch();
  const navigate = useNavigate({ from: '/cases/' });
  const me = useMe();
  const mayStart = permissionsForRoles(me.roles).includes('task.create');
  const query = useInfiniteQuery(caseListPagesQuery({ view, q, domain, state, processType }));
  const capabilities = useQuery({ ...capabilitiesQuery, meta: { silent: true } });
  const domains = [...new Set((capabilities.data?.items ?? []).map((c) => c.domain))].sort();
  const definitions = useQuery(processDefinitionsQuery);
  const open = useQuery(openDeadlinesQuery);
  const now = Date.now();
  const nameOf = usePrincipalNames();
  // 26 §6.6: rows read "due 4 Nov"; principal ids become names, role ids labels (outcome-model defaults).
  const outcomeCtx: OutcomeContext = { now, nameOf, formatDate: (iso) => formatShortDate(iso, now) };
  const sortBy: CaseListSort = sort === 'due' ? 'due' : 'newest';
  const deadlines = caseDeadlines(open.data?.items ?? [], open.data?.requests ?? [], now);
  const titles = new Map<string, string>([
    [AGENT_RUN_PROCESS, 'Agent run'],
    ...(definitions.data?.items ?? []).map((d) => [d.meta.name, d.meta.title ?? d.meta.name] as const),
  ]);
  const types = [...new Set([...(definitions.data?.items ?? []).map((d) => d.meta.name)])].sort();
  const loaded = query.data?.pages.flatMap((p) => p.items) ?? [];
  const items = sortCaseRows(loaded, sortBy);
  const caseTitles = new Map(loaded.map((c) => [c.caseId, c.title ?? titles.get(c.processType)] as const));
  const waiting = new Set(query.data?.pages.flatMap((p) => p.waitingOnMe ?? []) ?? []);
  const filtered = !!(state || processType || q || domain || view !== 'all');
  return (
    <>
      <PageHeader
        title="Cases"
        description="A case collects the processes, agents, work, approvals and effects of one business matter. You see the cases your scope allows."
        actions={
          // Review 10: starting a case needs `task.create` (the server answers 403 otherwise).
          mayStart ? (
            <Button
              variant="primary"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() => void navigate({ search: (s) => ({ ...s, new: 'case' }) })}
            >
              New case
            </Button>
          ) : undefined
        }
      >
        <Segmented aria-label="Case views" className="mt-5">
          {VIEWS.map((v) => (
            <Link
              key={v.id}
              to="/cases"
              search={(prev) => ({ ...prev, view: v.id === 'all' ? undefined : v.id })}
              replace
              className={segmentClass('wb-btn')}
              aria-current={view === v.id ? 'page' : undefined}
              data-view={v.id}
            >
              {v.label}
            </Link>
          ))}
        </Segmented>
        <FilterBar className="mt-3" legend="Case filters">
          <SearchInput
            id="case-search"
            label="Search cases by title or business key"
            defaultValue={q ?? ''}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const value = e.currentTarget.value.trim();
              void navigate({ search: (s) => ({ ...s, q: value || undefined }), replace: true });
            }}
            onBlur={(e) => {
              const value = e.currentTarget.value.trim();
              if (value !== (q ?? ''))
                void navigate({ search: (s) => ({ ...s, q: value || undefined }), replace: true });
            }}
          />
          <FilterSelect
            id="case-domain"
            label="Domain"
            value={domain ?? ''}
            onChange={(e) =>
              void navigate({ search: (s) => ({ ...s, domain: e.target.value || undefined }), replace: true })
            }
          >
            <option value="">Any domain</option>
            {[...new Set([...domains, ...(domain ? [domain] : [])])].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            id="case-state"
            label="State"
            value={state ?? ''}
            onChange={(e) =>
              void navigate({
                search: (s) => ({ ...s, caseState: (e.target.value || undefined) as CaseState | undefined }),
                replace: true,
              })
            }
          >
            <option value="">Any state</option>
            {CaseState.options.map((s) => (
              <option key={s} value={s}>
                {CASE_STATE_LABELS[s]}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            id="case-process-type"
            label="Process"
            value={processType ?? ''}
            onChange={(e) =>
              void navigate({
                search: (s) => ({ ...s, processType: e.target.value || undefined }),
                replace: true,
              })
            }
          >
            <option value="">Any process</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {titles.get(t) ?? t}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            id="case-sort"
            label={ow('list.sort.label')}
            value={sortBy}
            onChange={(e) =>
              void navigate({
                search: (s) => ({ ...s, sort: e.target.value === 'due' ? ('due' as const) : undefined }),
                replace: true,
              })
            }
          >
            <option value="newest">{ow('list.sort.newest')}</option>
            <option value="due">{DUE_SOONEST_LABEL()}</option>
          </FilterSelect>
        </FilterBar>
      </PageHeader>
      <div className={PAGE}>
        {view === 'waiting' && (
          <div className={`overflow-hidden ${CARD}`}>
            <WaitingOnYouSection ctx={outcomeCtx} caseTitleOf={(id) => caseTitles.get(id)} />
          </div>
        )}
        <div className={`overflow-hidden ${CARD}`}>
          {query.isPending ? (
            <SkeletonRows rows={5} label="Loading cases" />
          ) : query.isError ? (
            <div className="p-4">
              <QueryError error={query.error} onRetry={() => void query.refetch()} />
            </div>
          ) : items.length === 0 ? (
            <EmptyState title={view === 'waiting' ? 'Nothing waits on you' : 'No cases'}>
              {filtered
                ? 'Try another view or filter.'
                : 'Cases appear here when you start an agent or a process.'}
            </EmptyState>
          ) : (
            <Table aria-label="Cases" data-testid="cases-table">
              <thead>
                <tr>
                  <th scope="col">Case</th>
                  <th scope="col">Process</th>
                  <th scope="col">Scope</th>
                  <th scope="col">State</th>
                  <th scope="col">Updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.caseId}
                    data-testid="case-row"
                    data-case-id={c.caseId}
                    data-process-type={c.processType}
                  >
                    <td className="md:max-w-[26rem]">
                      <Link
                        to="/cases/$caseId"
                        params={{ caseId: c.caseId }}
                        className="font-medium text-ink hover:underline"
                      >
                        {c.title ?? titles.get(c.processType) ?? c.processType}
                      </Link>
                      {waiting.has(c.caseId) && (
                        <Badge tone="accent" dot className="ml-2" data-testid="waiting-on-you">
                          Waiting on you
                        </Badge>
                      )}
                      {c.state === 'on_hold' && c.hold && (
                        <div className="mt-0.5 text-[12.5px] text-warn-ink" data-testid="case-hold-note">
                          On hold: {c.hold.reason}
                        </div>
                      )}
                      <CaseWaitingOnLine value={c} ctx={outcomeCtx} />
                      <CaseDeadlineNote value={deadlines.get(c.caseId)} now={now} />
                      {c.businessKeys.length > 0 && (
                        <div className="text-[12px] text-ink-3">{c.businessKeys.join(', ')}</div>
                      )}
                    </td>
                    <td className="text-[13px]">
                      {titles.get(c.processType) ?? c.processType}
                      <div className="font-mono text-[12px] text-ink-3">{c.processType}</div>
                    </td>
                    <td className="text-[13px] text-ink-2">
                      {Object.entries(c.scope)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(', ') || 'tenant-wide'}
                    </td>
                    <td>
                      <CaseOutcomeBadge value={c} ctx={outcomeCtx} />
                    </td>
                    <td
                      className="whitespace-nowrap text-[13px] text-ink-2"
                      title={formatDateTime(c.updatedAt)}
                    >
                      {formatRelative(c.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {query.hasNextPage && (
            <div className="flex justify-center border-t border-line p-3">
              <Button
                variant="secondary"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
                data-testid="cases-more"
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>
      <StartCaseDrawer
        open={starting !== undefined}
        onOpenChange={(open) => !open && void navigate({ search: ({ new: _new, ...s }) => s, replace: true })}
        initialCapability={starting && starting !== 'case' ? starting : undefined}
      />
    </>
  );
}

/** The most urgent open deadline of a case (21d item 3), when it has one. */
function CaseDeadlineNote({ value, now }: { value: CaseDeadline | undefined; now: number }) {
  if (!value) return null;
  return (
    <div className="mt-1" data-testid="case-deadline">
      <DeadlineBadge deadline={value.deadline} overdue={value.overdue} now={now} />
      {value.open > 1 && <span className="ml-1 text-[12px] text-ink-3">+{value.open - 1} more</span>}
    </div>
  );
}
